<template>
  <div class="overflow-x-auto bg-surface-default-mid py-16 scrollbar-none">
    <PlpContainer class="w-auto" :is-wide>
      <ul class="flex items-start gap-8 md:gap-16">
        <li
          v-for="criterion in criteria"
          :key="criterion.criterion"
          class="w-[288px] h-[320px] shrink-0"
        >
          <FinderSizeTile
            v-if="criterion.criterion === 'size' && sizeCriterion"
            v-model:selected-option-ids="selectedSizeOptionIds"
            :criterion="sizeCriterion"
            @select-range="handleSelectSizeRange"
          />
          <FinderUsageTile
            v-else-if="criterion.criterion === 'usage' && usageCriterion"
            v-model:selected-ids="selectedUsageIds"
            :criterion="usageCriterion"
            :options="usageOptions"
          />
          <FinderPriceTile
            v-else-if="criterion.criterion === 'budget' && budgetCriterion"
            v-model="priceModel"
            :criterion="budgetCriterion"
            :price-facet
          />
          <FinderConditionsTile
            v-else-if="
              criterion.criterion === 'conditions' && conditionsCriterion
            "
            v-model="selectedConditionThreshold"
            :criterion="conditionsCriterion"
          />
          <FinderCard
            v-else
            :title="criterion.title"
            :tooltip="criterion.tooltip"
          />
        </li>
      </ul>
    </PlpContainer>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'

import { findCriterion, useFinder } from '../../composables/useFinder/useFinder'
import { getMacbookFinderConfig } from '../../composables/useFinder/useFinder.config.macbook'
import type { FinderOption } from '../../composables/useFinder/useFinder.types'
import PlpContainer from '../PlpContainer/PlpContainer.vue'

import FinderCard from './components/FinderCard/FinderCard.vue'
import FinderConditionsTile from './components/FinderConditionsTile/FinderConditionsTile.vue'
import FinderPriceTile from './components/FinderPriceTile/FinderPriceTile.vue'
import FinderSizeTile from './components/FinderSizeTile/FinderSizeTile.vue'
import FinderUsageTile from './components/FinderUsageTile/FinderUsageTile.vue'

const props = defineProps<{
  facets: Facet[]
  priceFacet?: UiPriceFacet
  filtersInput?: Record<string, string[]>
}>()

const priceModel = defineModel<[number, number] | undefined>('price')

const i18n = useI18n()

const config = getMacbookFinderConfig(i18n)
const sizeCriterion = findCriterion(config, 'size')
const usageCriterion = findCriterion(config, 'usage')
const budgetCriterion = findCriterion(config, 'budget')
const conditionsCriterion = findCriterion(config, 'conditions')

const criteria = config.criteria.map((c) => ({
  criterion: c.kind,
  title: c.title,
  tooltip: 'tooltip' in c ? c.tooltip : undefined,
}))

const USAGE_FACET_NAME = 'usages.label'

const usageFacet = computed(() =>
  props.facets.find((f) => f.name === USAGE_FACET_NAME),
)

const usageOptions = computed<FinderOption[]>(() => {
  const facet = usageFacet.value
  if (!usageCriterion || !facet) return []

  return usageCriterion.options
    .map((option) => {
      const facetValue = facet.values.find((v) => v.name === option.values[0])

      return facetValue
        ? {
            id: option.id,
            label: facetValue.label,
            values: option.values,
          }
        : null
    })
    .filter((o): o is FinderOption => o !== null)
})

const finder = props.filtersInput
  ? useFinder(config, props.filtersInput, priceModel)
  : null

const selectedSizeOptionIds = computed<string[]>({
  get() {
    if (!finder || !sizeCriterion) return []

    return sizeCriterion.options
      .filter((option) => finder.isOptionSelected(sizeCriterion, option.id))
      .map((option) => option.id)
  },
  set(optionIds) {
    if (!finder || !sizeCriterion) return

    const values = sizeCriterion.options
      .filter((option) => optionIds.includes(option.id))
      .flatMap((option) => option.values)

    finder.setCriterionValues(sizeCriterion, values)
  },
})

function handleSelectSizeRange([min, max]: [number, number]) {
  if (!finder || !sizeCriterion) return

  if (min === sizeCriterion.rangeMin && max === sizeCriterion.rangeMax) {
    finder.setCriterionValues(sizeCriterion, [])

    return
  }

  const inRangeValues = sizeCriterion.options
    .filter((option) => {
      const numericId = Number.parseInt(option.id, 10)

      return Number.isFinite(numericId) && numericId >= min && numericId <= max
    })
    .flatMap((option) => option.values)

  finder.setCriterionValues(sizeCriterion, inRangeValues)
}

const selectedUsageIds = computed<string[]>({
  get() {
    if (!finder || !usageCriterion) return []

    return usageCriterion.options
      .filter((option) => finder.isOptionSelected(usageCriterion, option.id))
      .map((option) => option.id)
  },
  set(optionIds) {
    if (!finder || !usageCriterion) return

    const values = usageCriterion.options
      .filter((option) => optionIds.includes(option.id))
      .flatMap((option) => option.values)

    finder.setCriterionValues(usageCriterion, values)
  },
})

const selectedConditionThreshold = computed<string | undefined>({
  get() {
    if (!finder || !conditionsCriterion) return undefined

    const threshold = conditionsCriterion.options.find((option) =>
      finder.isOptionSelected(conditionsCriterion, option.id),
    )

    return threshold?.id
  },
  set(optionId) {
    if (!finder || !conditionsCriterion) return

    if (optionId === undefined) {
      finder.setCriterionValues(conditionsCriterion, [])

      return
    }

    if (selectedConditionThreshold.value === optionId) {
      finder.setCriterionValues(conditionsCriterion, [])

      return
    }

    const clickedIndex = conditionsCriterion.options.findIndex(
      (option) => option.id === optionId,
    )
    if (clickedIndex === -1) return

    const expandedValues = conditionsCriterion.options
      .slice(clickedIndex)
      .flatMap((option) => option.values)

    finder.setCriterionValues(conditionsCriterion, expandedValues)
  },
})

const experiments = useExperiments()
const isWide =
  experiments['experiment.plpWideContainer'] === 'withWideContainer'
</script>
